import React, { useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import _get from 'lodash/get';
import { useDispatch, useSelector } from 'react-redux';
import { useOptimizelyTrack } from 'reaxl-optimizely';
import { PageSection } from 'reaxl';
import { savedInventoryDuck, dealerLotDuck } from '@/ducks';
import useELotCheck from '@/hooks/useELotCheck';
import { dealerLotAnalytics } from '@/analytics';

export const DealerLot = dynamic(() => import('reaxl-organisms/es/DealerLot'), { ssr: false });

const DealerLotSingleContainer = React.memo(({
    searchZip,
    pageName,
    mockQueries,
    isHomepage,
    makeCode,
    dealerId, // this is 'dealerId' from url query string (aka 'ownerId' in API response)
}) => {
    const dispatch = useDispatch();
    const optimizelyTrack = useOptimizelyTrack();
    const [impressionSent, setImpressionSent] = useState(false);

    const owner = useSelector(dealerLotDuck.selectors.getOwner);
    const listings = useSelector(dealerLotDuck.selectors.getListings);
    const location = useSelector(dealerLotDuck.selectors.getLocation);
    const savedListingIds = useSelector(savedInventoryDuck.selectors.getSavedInventoryIds);

    const haveListings = listings?.length > 0;
    const firstListing = listings[0] || {};

    useELotCheck({
        searchZip,
        mockQueries,
        // make Page
        makeCode,
        dealerId,
    });

    useEffect(() => {
        if (isHomepage && haveListings && !impressionSent) {
            optimizelyTrack('hp_elot_imp');
            setImpressionSent(true);
        }
    }, [isHomepage, impressionSent, haveListings, optimizelyTrack]);

    const handleReady = (index, selectedCells) => {
        const selectedListings = listings.slice(index, (index + selectedCells.length));
        dealerLotAnalytics.fireReaxleLotContainerImpression(firstListing, { pageName });
        dealerLotAnalytics.fireReaxleLotCarouselImpressions(selectedListings, { pageName }, index); // index is always 0 here, from reaxl
    };

    const handleSettle = (index, direction, width, selectedCells) => {
        const startingIndex = selectedCells.length * index; // index increments by 1 when carousel slides over one page
        const selectedListings = listings.slice(startingIndex, (startingIndex + selectedCells.length));
        dealerLotAnalytics.fireReaxleLotCarouselImpressions(selectedListings, { pageName }, startingIndex);
    };

    const handleInventoryListingClick = (event, selectedListing, index) => {
        dealerLotAnalytics.fireReaxleLotInventoryClick(event, selectedListing, { pageName }, index);

        if (isHomepage) {
            optimizelyTrack('hp_elot_click');
        }

        const website = _get(selectedListing, 'website.href', '');
        if (website) {
            window.location.assign(`${website}&clickType=elot`);
        }
    };

    const handleDealerLogoClick = (event, listingOwner) => {
        dealerLotAnalytics.fireReaxleLotTileClick(event, listingOwner);
        const website = _get(listingOwner, 'website.href', false);
        if (website) {
            window.open(website);
        }
    };

    const handleCarouselArrowClick = (event, direction) => {
        const isRecentSearches = false;
        dealerLotAnalytics.fireReaxleLotArrowClick(event, direction, isRecentSearches);
    };

    const handleSave = (event, selectedListing, isSaved) => {
        // The event must be 'persisted' to be referenced in asynchronous functions like to one below; otherwise its
        // properties may be overwritten by React before the asynchronous function has a chance to read them.
        // See https://reactjs.org/docs/events.html
        if (event && event.persist) {
            event.persist();
        }

        dealerLotAnalytics.fireReaxleLotSaveClick(event, selectedListing, isSaved, 'dl_v_lstg');

        if (isSaved) {
            dispatch(savedInventoryDuck.creators.unsaveInventoryItem(selectedListing));
        } else {
            dispatch(savedInventoryDuck.creators.saveInventoryItem(selectedListing));
        }
    };

    return haveListings && (
        <PageSection
            data-cmp="DealerLot"
            uiContext="section-2"
            headingComponentClass="h2"
            headingClassName="margin-horizontal-3 text-primary"
            sectionContainerClassNames="row padding-vertical-5"
            alignment="left"
            offset={1}
        >
            <DealerLot
                owner={owner}
                location={location}
                listings={listings}
                onInventoryListingClick={handleInventoryListingClick}
                onTileClick={handleDealerLogoClick}
                onNextButtonClick={(e) => handleCarouselArrowClick(e, 'next')}
                onPrevButtonClick={(e) => handleCarouselArrowClick(e, 'previous')}
                onSaveToggle={handleSave}
                savedListingIds={savedListingIds}
                handleSettle={handleSettle}
                carouselSettings={{
                    onReady: handleReady,
                }}
            />
        </PageSection>
    );
});

export default DealerLotSingleContainer;
